import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Paper, Typography, Button, Stack, Table, TableHead, TableRow, TableCell,
  TableBody, Chip, IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, FormControlLabel, Checkbox, Alert, Tooltip,
} from "@mui/material";
import { Edit, Delete, Add, Lock } from "@mui/icons-material";
import { listChannels, createChannel, updateChannel, deleteChannel, listGroups, ChannelRow } from "../../api";
import { useAuth } from "../../auth/AuthContext";
import GroupPicker from "../../components/GroupPicker";

type Draft = {
  _new: boolean; id?: number; name: string; display_name: string; color: string;
  is_global: boolean; group_ids: number[];
};

export default function ChannelsAdmin() {
  const { isSuperAdmin, me } = useAuth();
  const [rows, setRows] = useState<ChannelRow[]>([]);
  const [groupOpts, setGroupOpts] = useState<{ id: number; name: string }[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);

  const groupName = (id: number) => groupOpts.find((g) => g.id === id)?.name ?? `#${id}`;

  const load = useCallback(async () => {
    setErr(null);
    try { setRows(await listChannels()); } catch (e: any) { setErr(e.message); }
    if (isSuperAdmin) {
      try { const g = await listGroups(); setGroupOpts(g.map((x) => ({ id: x.id, name: x.name }))); } catch { /* ignore */ }
    } else {
      setGroupOpts((me?.groups || []).filter((g) => g.role === "admin").map((g) => ({ id: g.id, name: g.name })));
    }
  }, [isSuperAdmin, me]);
  useEffect(() => { load(); }, [load]);

  const openNew = () => setDraft({ _new: true, name: "", display_name: "", color: "", is_global: false, group_ids: [] });
  const openEdit = (c: ChannelRow) => setDraft({
    _new: false, id: c.id, name: c.name, display_name: c.display_name || "",
    color: c.color || "", is_global: c.is_global, group_ids: c.groups || [],
  });

  const save = async () => {
    if (!draft) return;
    setErr(null);
    try {
      if (draft._new) {
        await createChannel({
          name: draft.name.trim(), display_name: draft.display_name || undefined,
          color: draft.color || undefined, is_global: draft.is_global, group_ids: draft.group_ids,
        });
      } else {
        await updateChannel(draft.id!, {
          display_name: draft.display_name || undefined, color: draft.color || undefined,
          is_global: isSuperAdmin ? draft.is_global : undefined, group_ids: draft.group_ids,
        });
      }
      setDraft(null);
      await load();
    } catch (e: any) { setErr(e.message); }
  };

  const remove = async (c: ChannelRow) => {
    if (!window.confirm(`Delete channel "${c.name}"? Its EPG rows will stop rendering.`)) return;
    setErr(null);
    try { await deleteChannel(c.id); await load(); } catch (e: any) { setErr(e.message); }
  };

  return (
    <Box>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h4">Channels</Typography>
          <Button variant="contained" startIcon={<Add />} onClick={openNew}>New channel</Button>
        </Stack>
        {err && <Alert severity="error" sx={{ mt: 2 }}>{err}</Alert>}
      </Paper>

      <Paper>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell><TableCell>Display</TableCell>
              <TableCell>Scope</TableCell><TableCell>Groups</TableCell><TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((c) => (
              <TableRow key={c.id} hover>
                <TableCell>{c.name}</TableCell>
                <TableCell>{c.display_name || ""}</TableCell>
                <TableCell>
                  {c.is_global
                    ? <Chip size="small" color="info" label="global" />
                    : <Chip size="small" variant="outlined" label="group" />}
                </TableCell>
                <TableCell>
                  <Stack direction="row" gap={0.5} flexWrap="wrap">
                    {(c.groups || []).map((g) => <Chip key={g} size="small" label={groupName(g)} />)}
                  </Stack>
                </TableCell>
                <TableCell align="right">
                  {c.can_write ? (
                    <>
                      <IconButton size="small" onClick={() => openEdit(c)} title="Edit"><Edit fontSize="small" /></IconButton>
                      <IconButton size="small" onClick={() => remove(c)} title="Delete"><Delete fontSize="small" /></IconButton>
                    </>
                  ) : (
                    <Tooltip title="Read-only for you"><Lock fontSize="small" sx={{ opacity: 0.5 }} /></Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && <TableRow><TableCell colSpan={5} align="center" sx={{ py: 4 }}>No channels.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Paper>

      <Dialog open={!!draft} onClose={() => setDraft(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{draft?._new ? "New channel" : `Edit ${draft?.name}`}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Name (id, lowercase)" fullWidth value={draft?.name || ""} disabled={!draft?._new}
              onChange={(e) => draft && setDraft({ ...draft, name: e.target.value })}
              helperText={draft?._new ? "Immutable after creation; EPG rows match on this." : "Cannot be changed."} />
            <TextField label="Display name" fullWidth value={draft?.display_name || ""}
              onChange={(e) => draft && setDraft({ ...draft, display_name: e.target.value })} />
            <TextField label="Color (hex, optional)" fullWidth value={draft?.color || ""}
              onChange={(e) => draft && setDraft({ ...draft, color: e.target.value })} />
            <FormControlLabel
              control={<Checkbox checked={!!draft?.is_global} disabled={!isSuperAdmin}
                onChange={(e) => draft && setDraft({ ...draft, is_global: e.target.checked })} />}
              label={`Global (national feed)${isSuperAdmin ? "" : " - super-admin only"}`} />
            <GroupPicker label="Shared with groups" options={groupOpts}
              value={draft?.group_ids || []} onChange={(v) => draft && setDraft({ ...draft, group_ids: v })} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDraft(null)}>Cancel</Button>
          <Button variant="contained" onClick={save} disabled={!draft?.name?.trim()}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
